// @ts-strict-ignore
import { Icon, Spinner } from '@blueprintjs/core'
import classNames, { Value as ClassValue } from 'classnames'
import { format, isThisYear } from 'date-fns'
import throttle from 'lodash.throttle'
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import uPlot from 'uplot'
import { percentage } from '../../util/formatters'
import { EmptyState } from '../ui/empty-state'
import { ChartTag } from './chart_tag'

type ChartProps = {
    title: string
    // Data is expected as four arrays:
    // - 1. current range unix timestamps
    // - 2. current range values
    // - 3. previous range unix timestamps
    // - 4. previous range values
    // Value arrays may contain nulls, which uPlot renders as gaps.
    data: (number | null)[][]
    height?: number
    className?: ClassValue
    formatter?: (number) => string
    current: number
    previous: number
    lowerIsBetter?: boolean
    sync?: uPlot.SyncPubSub
    screenshotMode?: boolean
    empty?: any

    // when true, we will not display a chart with 0 values but will instead display a EmptyState
    showNoDataIcon?: boolean
    compare?: boolean
    loading?: boolean
}

const DefaultEmpty = <EmptyState icon={<Icon icon="search" size={60} />}>No data found</EmptyState>

export const Chart = ({
    title,
    data = [[], [], [], []],
    height = 100,
    className,
    formatter = (v) => v,
    current,
    previous,
    lowerIsBetter = false,
    sync,
    screenshotMode = false,
    empty = 'default',
    showNoDataIcon = false,
    compare = false,
    loading = true,
}: ChartProps) => {
    const [isDataEmpty, setIsDataEmpty] = useState<boolean>(true)
    const [uncontrolledIndex, setUncontrolledIndex] = useState<number | null>(null)
    const [uPlot, setUPlot] = useState(null)
    const [cWidth, setCWidth] = useState<number | null>(null)
    const container = useRef<HTMLDivElement>()
    const chartRef = useRef(null)

    // Track container width with ResizeObserver to avoid race conditions
    useEffect(() => {
        if (!container.current) return

        const resizeObserver = new ResizeObserver((entries) => {
            const width = entries[0].contentRect.width
            if (width > 0) {
                setCWidth(width)
            }
        })

        resizeObserver.observe(container.current)
        return () => resizeObserver.disconnect()
    }, [])

    useEffect(() => {
        if (uPlot && cWidth) {
            uPlot.setSize({
                width: cWidth,
                height,
            })
        }
    }, [cWidth, height])

    useEffect(() => {
        if (sync && uPlot && !sync.plots.includes(uPlot)) {
            sync.sub(uPlot)
        }
    }, [uPlot])

    let prevIndex = null
    const updateIndex = (idx) => {
        if (prevIndex !== idx) {
            setUncontrolledIndex(idx === null ? null : parseInt(idx))
        }
        prevIndex = idx !== null ? parseInt(idx) : null
    }

    const effectiveWidth = cWidth ?? 100
    const options = useMemo(
        () => ({
            id: 'overview-chart',
            width: effectiveWidth,
            height,
            cursor: {
                y: false,
                lock: true,
                sync: {
                    key: sync ? sync.key : null,
                },
            },
            axes: [
                {
                    show: false,
                    grid: {
                        show: false,
                    },
                    ticks: {
                        size: 0,
                    },
                    // splits: [data[0][0], data[0][data[0].length - 1]],
                    // values: (_, values) => values.map((v) => {
                    //     if (isSameDay(v, now)) {
                    //         return 'Today'
                    //     } else {
                    //         return formatTimestamp(v, true)
                    //     }
                    // }),
                },
                {
                    show: false,
                },
            ],
            legend: {
                show: false,
            },
            scales: {
                x: {
                    time: true,
                },
            },
            hooks: {
                init: [(uplot) => setUPlot(uplot)],
                setCursor: [
                    throttle((uPlot) => updateIndex(uPlot.cursor.idx), 100, { leading: true }),
                ],
            },
            series: [
                { show: false },
                { show: false },
                {
                    label: 'Previous',
                    stroke: '#ADD3FF',
                    fill: (u, seriesIdx) => {
                        const s = u.series[seriesIdx]
                        const sc = u.scales[s.scale]

                        let min = Infinity
                        let max = -Infinity

                        // get in-view y range for this scale
                        u.series.forEach((ser) => {
                            if (ser.show && ser.scale == s.scale) {
                                min = Math.min(min, ser.min)
                                max = Math.max(max, ser.max)
                            }
                        })

                        let range = max - min

                        if (range == 0) {
                            range = sc.max - sc.min
                            min = sc.min
                        }

                        return scaleGradient(u, s.scale, 1, [
                            [min + range * 0.0, 'rgba(255, 255, 255, 0.5)'],
                            [min + range * 1.0, 'rgba(214, 233, 255, 0.5)'],
                        ])
                    },
                    points: {
                        size: 1,
                    },
                },
                {
                    show: false,
                },
                {
                    label: 'Current',
                    stroke: '#2974CC',
                    fill: (u, seriesIdx) => {
                        const s = u.series[seriesIdx]
                        const sc = u.scales[s.scale]

                        let min = Infinity
                        let max = -Infinity

                        // get in-view y range for this scale
                        u.series.forEach((ser) => {
                            if (ser.show && ser.scale == s.scale) {
                                min = Math.min(min, ser.min)
                                max = Math.max(max, ser.max)
                            }
                        })

                        let range = max - min

                        if (range == 0) {
                            range = sc.max - sc.min
                            min = sc.min
                        }

                        return scaleGradient(u, s.scale, 1, [
                            [min + range * 0.0, 'rgba(255, 255, 255, 0.5)'],
                            [min + range * 1.0, 'rgba(173, 211, 255, 0.1)'],
                        ])
                    },
                    points: {
                        size: 1,
                    },
                },
            ],
        }),
        [effectiveWidth, height],
    )

    useEffect(() => {
        const initialState = true
        for (let i = 0; i < data.length; i++) {
            const arr = data[i]
            if (arr.length > 0) {
                setIsDataEmpty(false)
                return
            }
        }

        setIsDataEmpty(initialState)
    }, [data])

    const { startDate, endDate } = useMemo(() => {
        const startDate = formatTimestamp(data[0][0], true)
        const endDate = formatTimestamp(data[0][data[0].length - 1], true)

        return {
            startDate,
            endDate,
        }
    }, [data])

    const { avg1, avg2, currentRange, previousRange } = useMemo(() => {
        return {
            avg1: current ? current : data[1].reduce((prev, curr) => prev + curr, 0),
            avg2: previous ? previous : data[3].reduce((prev, curr) => prev + curr, 0),
            currentRange: <RangeLabel startTs={data[0][0]} endTs={data[0][data[0].length - 1]} />,
            previousRange: <RangeLabel startTs={data[2][0]} endTs={data[2][data[2].length - 1]} />,
        }
    }, [current, previous, data])

    const { badge, formattedDisplay1, formattedDisplay2, currentHoverDate, previousHoverDate } =
        useMemo(() => {
            const display1 =
                typeof uncontrolledIndex === 'number' ? data[1][uncontrolledIndex] : avg1
            const display2 =
                typeof uncontrolledIndex === 'number' ? data[3][uncontrolledIndex] : avg2

            return {
                display1,
                display2,
                badge: (
                    <Badge
                        lowerIsBetter={lowerIsBetter}
                        className={classNames('ml-2 font-bold', { blur: screenshotMode })}
                        a={display1}
                        b={display2}
                    />
                ),
                formattedDisplay1: formatter(display1),
                formattedDisplay2: formatter(display2),
                currentHoverDate:
                    typeof uncontrolledIndex === 'number' && data[0]?.[uncontrolledIndex]
                        ? formatTimestamp(data[0]?.[uncontrolledIndex], true)
                        : null,
                previousHoverDate:
                    typeof uncontrolledIndex === 'number' && data[2]?.[uncontrolledIndex]
                        ? formatTimestamp(data[2]?.[uncontrolledIndex], true)
                        : null,
            }
        }, [data, uncontrolledIndex, avg1, avg2, screenshotMode])

    const chart = useMemo(() => {
        if (data.length == 0) {
            return null
        }

        let notEmpty = 0
        data.forEach((array) => {
            if (array.length > 0) {
                notEmpty++
            }
        })

        // transform the data a bit
        const temp = [...data]
        const output = []

        if (notEmpty > 2) {
            output.push(temp[0], temp[2], temp[3], temp[0], temp[1])
        } else {
            output.push(temp[0], [], [], temp[0], temp[1])
        }

        return <MyChart chartRef={chartRef} options={options} data={output} />
    }, [options, data, chartRef])

    return (
        <div
            style={{
                // border: '1px solid #CDD6DF',
                // borderRadius: '5px',
                padding: '15px 15px 0 15px',
            }}
            className={classNames(
                'flex bg-white',
                // A wrapped range label makes the card taller; the grid row grows with it.
                isDataEmpty || loading ? 'h-[182px]' : 'min-h-[182px]',
                isDataEmpty ? 'justify-center' : 'flex-col',
                !loading && 'items-start',
                className,
            )}
            ref={container}
        >
            <>
                {isDataEmpty || loading ? (
                    <div
                        className="p-1 flex flex-col h-full items-center justify-center"
                        style={{ marginTop: '-15px' }}
                    >
                        <EmptyState
                            icon={loading ? <Spinner /> : 'search'}
                            title={loading ? 'Loading' : 'No data found'}
                            description={
                                <span className="text-sm">
                                    {loading
                                        ? 'Please wait...'
                                        : 'Please choose different filters and/or date range'}
                                </span>
                            }
                        />
                    </div>
                ) : (
                    <>
                        <div className="whitespace-nowrap">
                            <span
                                style={{ color: '#143A66' }}
                                className="font-bold mr-2 text-base font-semibold"
                            >
                                {title}
                            </span>
                            {!isDataEmpty && badge}
                        </div>
                        {(!showNoDataIcon || !isDataEmpty) && (
                            <Fragment>
                                <div className="flex justify-between items-center w-full mb-1">
                                    <div className="flex flex-col items-start">
                                        <div
                                            className={classNames('text-lg text-mjDark-1400', {
                                                blur: screenshotMode,
                                            })}
                                        >
                                            {formattedDisplay1}
                                        </div>
                                        <DateLabel
                                            range={currentRange}
                                            hoverDate={currentHoverDate}
                                            dotClassName="bg-mjBlue-600"
                                            className={classNames({ blur: screenshotMode })}
                                        />
                                    </div>
                                    {compare && (
                                        <div className="flex flex-col items-end">
                                            <div
                                                className={classNames('text-lg text-mjDark-1400', {
                                                    blur: screenshotMode,
                                                })}
                                            >
                                                {formattedDisplay2}
                                            </div>
                                            <DateLabel
                                                range={previousRange}
                                                hoverDate={previousHoverDate}
                                                dotClassName="bg-mjBlue-200"
                                                className={classNames('justify-items-end', {
                                                    blur: screenshotMode,
                                                })}
                                            />
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <div className="pb-2">{chart}</div>
                                    {/* <div
                                        style={{ marginLeft: '-15px', marginRight: '-15px' }}
                                        className="border-t flex flex-row justify-between py-1.5 px-2.5"
                                    >
                                        <span className="text-sm text-mjDark-600">{startDate}</span>
                                        <span className="text-sm text-mjDark-600">{endDate}</span>
                                    </div> */}
                                </div>
                            </Fragment>
                        )}
                        {showNoDataIcon && isDataEmpty && (
                            <div className="w-full h-full flex items-center justify-center">
                                {empty === 'default' ? DefaultEmpty : empty}
                            </div>
                        )}
                    </>
                )}
            </>
        </div>
    )
}

// A date outside the viewer's current local year also shows its two-digit year (Dec 31 '25).
export const formatTimestamp = (ts: number, toLong?: boolean): string => {
    if (!ts) {
        return ''
    }

    const date = new Date(toLong ? ts * 1000 : ts)
    return format(date, isThisYear(date) ? 'MMM d' : "MMM d ''yy")
}

// A range within one past year shows the year once, on its end (Dec 3 - Dec 19 '25).
export const formatRange = (startTs: number, endTs: number): [string, string] => {
    const start = formatTimestamp(startTs, true)
    const end = formatTimestamp(endTs, true)
    if (!startTs || !endTs) {
        return [start, end]
    }

    const startDate = new Date(startTs * 1000)
    const sameYear = startDate.getFullYear() === new Date(endTs * 1000).getFullYear()
    return [sameYear ? format(startDate, 'MMM d') : start, end]
}

// The range breaks onto a second line only between its two dates, and only when the card is too narrow.
const RangeLabel = ({ startTs, endTs }: { startTs: number; endTs: number }) => {
    const [start, end] = formatRange(startTs, endTs)
    return (
        <span className="whitespace-normal">
            <span className="whitespace-nowrap">{start} -</span>{' '}
            <span className="whitespace-nowrap">{end}</span>
        </span>
    )
}

const DateRow = ({ dotClassName, className = undefined, children }) => (
    <div className={classNames('col-start-1 row-start-1 flex flex-row items-center', className)}>
        <div className={classNames('w-1.5 h-1.5 rounded-full mr-1 shrink-0', dotClassName)} />
        <span className="text-mjDark-1000 opacity-50 whitespace-nowrap">{children}</span>
    </div>
)

// The hover date sits over the hidden range, so a wrapped range keeps the card height while hovering.
const DateLabel = ({ range, hoverDate, dotClassName, className }) => (
    <div className={classNames('text-xs grid', className)}>
        <DateRow dotClassName={dotClassName} className={hoverDate && 'invisible'}>
            {range}
        </DateRow>
        {hoverDate && <DateRow dotClassName={dotClassName}>{hoverDate}</DateRow>}
    </div>
)

type BadgeProps = {
    a: number
    b: number
    className: ClassValue
    lowerIsBetter: boolean
}

const Badge = ({ a, b, className, lowerIsBetter = false }: BadgeProps) => {
    const diff = b === 0 ? 1 : (a - b) / b
    let intent = null
    if (diff > 0) {
        intent = lowerIsBetter ? 'negative' : 'positive'
    } else if (diff < 0) {
        intent = lowerIsBetter ? 'positive' : 'negative'
    }

    return (
        <ChartTag intent={intent}>
            {diff > 0 ? '+' : ''}
            {percentage(diff * 100, 1)}
        </ChartTag>
    )
}

const MyChart = ({ data, options, chartRef }) => {
    useEffect(() => {
        const c = new uPlot(options, data, chartRef.current)
        return () => {
            c.destroy()
        }
    }, [data])

    return <div ref={chartRef} />
}

export default MyChart

export const scaleGradient = (u, scaleKey, ori, scaleStops, discrete = false) => {
    const scale = u.scales[scaleKey]
    const fallbackColor = scaleStops.at(-1)?.[1] ?? 'transparent'

    if (!Number.isFinite(scale?.min) || !Number.isFinite(scale?.max)) {
        return fallbackColor
    }

    // we want the stop below or at the scaleMax
    // and the stop below or at the scaleMin, else the stop above scaleMin
    let minStopIdx
    let maxStopIdx

    for (let i = 0; i < scaleStops.length; i++) {
        const stopVal = scaleStops[i][0]

        if (stopVal <= scale.min || minStopIdx == null) {
            minStopIdx = i
        }

        maxStopIdx = i

        if (stopVal >= scale.max) {
            break
        }
    }

    if (minStopIdx == maxStopIdx) {
        return scaleStops[minStopIdx][1]
    }

    let minStopVal = scaleStops[minStopIdx][0]
    let maxStopVal = scaleStops[maxStopIdx][0]

    if (minStopVal == -Infinity) {
        minStopVal = scale.min
    }

    if (maxStopVal == Infinity) {
        maxStopVal = scale.max
    }

    const minStopPos = u.valToPos(minStopVal, scaleKey, true)
    const maxStopPos = u.valToPos(maxStopVal, scaleKey, true)

    const range = minStopPos - maxStopPos

    if (
        !Number.isFinite(minStopPos) ||
        !Number.isFinite(maxStopPos) ||
        !Number.isFinite(range) ||
        range === 0
    ) {
        return fallbackColor
    }

    const can = document.createElement('canvas')
    const ctx = can.getContext('2d')

    let x0
    let y0
    let x1
    let y1

    if (ori == 1) {
        x0 = x1 = 0
        y0 = minStopPos
        y1 = maxStopPos
    } else {
        y0 = y1 = 0
        x0 = minStopPos
        x1 = maxStopPos
    }

    const grd = ctx.createLinearGradient(x0, y0, x1, y1)

    let prevColor

    for (let i = minStopIdx; i <= maxStopIdx; i++) {
        const s = scaleStops[i]

        const stopPos =
            i == minStopIdx
                ? minStopPos
                : i == maxStopIdx
                  ? maxStopPos
                  : u.valToPos(s[0], scaleKey, true)
        const pct = (minStopPos - stopPos) / range

        if (discrete && i > minStopIdx) {
            grd.addColorStop(pct, prevColor)
        }

        grd.addColorStop(pct, (prevColor = s[1]))
    }

    return grd
}
